import React, { useState } from "react";
import { ArrowUpRight, Mail, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";
import { profile } from "../mock/mock";

const ContactSection = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", project: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project) {
      toast({
        title: "Missing details",
        description: "Please fill out every field before hitting REC.",
      });
      return;
    }
    toast({
      title: "Signal received ●",
      description: `Thanks ${form.name.split(" ")[0]} — I'll be in touch within 48h.`,
    });
    setForm({ name: "", email: "", project: "" });
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 border-t border-[#1f1a15]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5 reveal">
          <div className="text-[11px] mono uppercase tracking-[0.3em] text-[#f5b14e]">
            ▶ Contact / Load a Blank Tape
          </div>
          <h2
            className="mt-4 text-5xl md:text-6xl leading-[0.95]"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Let’s make <br />
            <em className="not-italic text-[#f5b14e]">something worth</em> <br />
            rewinding.
          </h2>
          <p className="mt-6 text-[#f2ece1]/70 max-w-md leading-relaxed">
            Commissions, festival programming, and long lunches all welcome.
            Drop a note and I’ll get back with a treatment or a coffee spot.
          </p>

          <a
            href={`mailto:${profile.email}`}
            className="mt-8 inline-flex items-center gap-3 text-[15px] group"
          >
            <Mail className="w-4 h-4 text-[#f5b14e]" />
            <span className="underline underline-offset-4 decoration-[#2a221b] group-hover:decoration-[#f5b14e]">
              {profile.email}
            </span>
            <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
          </a>

          <div className="mt-8 flex flex-wrap gap-3">
            {profile.socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                className="text-[12px] mono uppercase tracking-widest px-3 py-2 rounded-full border border-[#2a221b] hover:border-[#f5b14e] hover:text-[#f5b14e] transition-colors"
              >
                {s.label} ↗
              </a>
            ))}
          </div>
        </div>

        <form
          onSubmit={submit}
          className="md:col-span-7 reveal rounded-2xl border border-[#2a221b] bg-[#120e0a] p-6 md:p-10"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] mono uppercase tracking-widest text-[#f2ece1]/55">
                Your Name
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ada Lovelace"
                className="mt-2 bg-[#0e0b09] border-[#2a221b] focus-visible:ring-[#f5b14e] text-[#f2ece1]"
              />
            </div>
            <div>
              <label className="text-[11px] mono uppercase tracking-widest text-[#f2ece1]/55">
                Email
              </label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@studio.com"
                className="mt-2 bg-[#0e0b09] border-[#2a221b] focus-visible:ring-[#f5b14e] text-[#f2ece1]"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="text-[11px] mono uppercase tracking-widest text-[#f2ece1]/55">
              Tell me about the project
            </label>
            <Textarea
              value={form.project}
              onChange={(e) => setForm({ ...form, project: e.target.value })}
              rows={6}
              placeholder="A 90-second brand film shot on Super 16, delivered in 6 weeks…"
              className="mt-2 bg-[#0e0b09] border-[#2a221b] focus-visible:ring-[#f5b14e] text-[#f2ece1] resize-none"
            />
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div className="text-[11px] mono text-[#f2ece1]/45">
              PROTECTED SIGNAL • END-TO-END
            </div>
            <Button
              type="submit"
              className="h-11 px-6 bg-[#f2ece1] text-[#0e0b09] hover:bg-[#f5b14e] rounded-full text-[13px] font-medium flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Hit REC
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
